<template>
  <div
    class="cb-dynamic-data-table"
    :class="{ 'rounded-2': !isScribbleChild && isEditMode }"
    :style="omit(style, ['paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight'])"
  >
    <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
    <EditableComponent
      v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :active="componentEditMode && componentEditMode?.id === id"
      :collapsible="!isScribbleChild"
      :duplicate="componentPermissions.canDuplicate"
      :edit="componentPermissions.canEdit"
      :exclude-stylables="
        isScribbleChild ? [ThemeComponentOptionEnum.SPACING, ThemeComponentOptionEnum.BORDER_RADIUS] : undefined
      "
      :parent-type="parentType"
      :remove="componentPermissions.canRemove"
      :resizable="isScribbleChild && !usedInSectionId"
      :resize-handles="[
        'top-left',
        'top-right',
        'center-left',
        'center-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ]"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      :show-only-bottom-toolbar="shouldShowOnlyBottomToolbar"
      :show-only-top-toolbar="shouldShowOnlyTopToolbar"
      :stylable="!usedInSectionId"
      @duplicate="duplicate(id)"
      @edit="setComponentEditMode(ComponentTypes.DynamicDataTable, id)"
      @remove="removeComponentById(id)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
    >
      <RawDynamicDataTable
        v-if="!isEmpty(data)"
        :id="id"
        :key="JSON.stringify(data)"
        :canvas-metadata="canvasMetadata?.config"
        :data="dataWithOverrides"
        :parent-type="parentType"
        :selected="componentSelectedMode && componentSelectedMode?.id === id"
        :style="style"
        :theme_meta="theme_meta"
        :used-in-section-id="usedInSectionId"
        @sortChanged="handleSortChanged"
        @style="emit('style', $event)"
      />
      <EditDynamicDataTable v-else :style="placeholderStyle" />
    </EditableComponent>
    <RawDynamicDataTable
      v-else
      :id="id"
      :canvas-metadata="canvasMetadata?.config"
      :data="dataWithOverrides"
      :parent-type="parentType"
      :style="style"
      :theme_meta="theme_meta"
      :used-in-section-id="usedInSectionId"
      @sortChanged="handleSortChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { type ColumnSort } from '@tanstack/table-core'
import { isEmpty, omit, pick } from 'lodash-es'
import { computed, toRefs, onMounted } from 'vue'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import EditDynamicDataTable from './DynamicDataTable.edit.vue'
import RawDynamicDataTable from './DynamicDataTable.raw.vue'

import {
  ComponentTypes,
  type ComponentNode,
  type DynamicDataTableProps,
  type DynamicDatatableProperty,
} from '@canvas-builder/types/canvas'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { type CanvasRetrieve } from '@openapi'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { type CanvasMetadataRetrieve } from '@lib/types/canvases'
import { useCoreApi } from '@lib/composables/useCoreApi'
import { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import { useScribbleChild, getDynamicDataTableScribbleConfig } from '@canvas-builder/composables/useScribbleChild'
import { useScribbleToolbar } from '@canvas-builder/composables/useScribbleToolbar'

interface DynamicDataTableUserSorting {
  field: string
  direction: 'asc' | 'desc'
}

interface DynamicDataTableCanvasMetadata {
  user_sorting?: DynamicDataTableUserSorting
}

const props = withDefaults(
  defineProps<{
    id: string
    data: DynamicDataTableProps
    children?: ComponentNode[]
    parentType?: ComponentTypes
    usedInSectionId?: CanvasRetrieve['id']
    when_used_in_section?: ComponentNode['when_used_in_section']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: Record<string, any>
    theme_meta?: ComponentNode['theme_meta']
  }>(),
  {
    children: () => [],
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: Record<string, any>, id?: ComponentNode['id']]
}>()

const { id, usedInSectionId, when_used_in_section } = toRefs(props)

const {
  activeCanvas,
  canvasMetadata,
  componentEditMode,
  componentSelectedMode,
  duplicate,
  isImpact,
  isEditMode,
  isViewOnlyMode,
  justAddedComponentId,
  mode,
  removeComponentById,
  setComponentEditMode,
  setCanvasMetadata,
  setComponentSelectedMode,
  setComponentSettingsMode,
} = useCanvas()
const { coreRestApiAxios } = useCoreApi()

const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const scribbleChildConfig = getDynamicDataTableScribbleConfig(props.id, props.parentType, props.style)
const { isScribbleChild, handleAddedToScribble } = useScribbleChild(scribbleChildConfig)
const { shouldShowOnlyTopToolbar, shouldShowOnlyBottomToolbar } = useScribbleToolbar(props.id)

const placeholderStyle = computed(() => {
  if (isScribbleChild.value) {
    return pick(props.style, ['width', 'height'])
  }

  return {
    ...props.style,
  }
})

const dataWithOverrides = computed(() => {
  const componentCanvasMetadata: DynamicDataTableCanvasMetadata =
    canvasMetadata.value?.presentation_component_metadata?.[props.id]

  const userSorting = componentCanvasMetadata?.user_sorting
  if (!userSorting) return props.data

  // Check if the field exists in data.properties and is sortable
  const sortableField = props.data.properties?.find((prop) => prop.key === userSorting.field && prop.sort.enabled)
  if (!sortableField) return props.data

  // Return data with updated sorting for the specified field
  return {
    ...props.data,
    properties: props.data.properties?.map((prop: DynamicDatatableProperty) =>
      prop.key === userSorting.field
        ? { ...prop, sort: { ...prop.sort, default: true, direction: userSorting.direction } }
        : { ...prop, sort: { ...prop.sort, default: false } },
    ),
  }
})

async function handleSortChanged(sorting: ColumnSort | null) {
  const field = sorting?.id ?? null
  const direction = sorting?.desc ? 'desc' : 'asc'

  if (!isImpact.value || !coreRestApiAxios?.value) {
    return
  }

  try {
    const updatedCanvasMetadata = await coreRestApiAxios.value.patch<unknown, CanvasMetadataRetrieve['config']>(
      `protected/canvases/${activeCanvas.value?.id}/metadata`,
      {
        config: {
          presentation_component_metadata: {
            [props.id]: field ? { user_sorting: { field, direction } } : undefined,
          },
        },
      },
    )

    setCanvasMetadata(updatedCanvasMetadata.config)
  } catch (error) {
    console.error('[DynamicDataTable.vue]: failed to update canvas metadata', error)
  }
}

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    handleAddedToScribble(emit)
    setComponentEditMode(ComponentTypes.DynamicDataTable, props.id)
  }
})
</script>
